import { Link } from 'react-router';
import { useCart, maxForSnapshot, itemKey, isPackItem } from '@/features/cart/CartContext';
import { CartPackLine } from '@/features/cart/CartPackLine';
import { useMeta } from '@/client/lib/useMeta';
import { formatMoney } from '@/lib/money';
import { EmptyState } from '@/components/States';
import { SectionHeader } from '@/components/SectionHeader';
import { useI18n } from '@/client/i18n/I18nContext';
import { lp } from '@/client/i18n/locale';

export function CartPage() {
  const { t } = useI18n();
  useMeta({ title: t('cart.pageTitle'), path: '/cart', noindex: true });
  const { items, subtotalCents, currency, setQty, remove } = useCart();

  if (items.length === 0) {
    return (
      <div className="container">
        <EmptyState
          title={t('cart.emptyTitle')}
          message={t('cart.emptyMsg')}
          action={<Link className="btn" to={lp('/shop/beauty')}>{t('cart.discoverBeauty')}</Link>}
        />
      </div>
    );
  }

  return (
    <div className="container">
      <SectionHeader as="h1" eyebrow={t('cart.title')} title={t('cart.pageTitle')} />
      <div style={{ paddingBottom: 'var(--space-8)' }}>
        {items.map((line) =>
          isPackItem(line) ? (
            <CartPackLine key={itemKey(line)} line={line} style={{ gridTemplateColumns: '80px 1fr auto' }} />
          ) : (
          <div className="cart-line" key={itemKey(line)} style={{ gridTemplateColumns: '80px 1fr auto' }}>
            <Link to={lp(`/shop/${line.product.category}/${line.product.slug}`)} className="cart-line__media">
              {line.product.image ? <img src={line.product.image} alt={line.product.name} /> : null}
            </Link>
            <div>
              {line.product.brand ? <span className="card__brand">{line.product.brand}</span> : null}
              <p style={{ fontFamily: 'var(--font-serif)', fontSize: 'var(--fs-md)' }}>{line.product.name}</p>
              {line.product.size ? (
                <span className="cart-line__size">{t('cart.size')} {line.product.size}</span>
              ) : null}
              <div className="qty" style={{ marginTop: 'var(--space-2)' }}>
                <button type="button" aria-label={t('a11y.qtyDecrease')} onClick={() => setQty(itemKey(line), line.quantity - 1)}>−</button>
                <span>{line.quantity}</span>
                <button type="button" aria-label={t('a11y.qtyIncrease')} disabled={line.quantity >= maxForSnapshot(line.product)} onClick={() => setQty(itemKey(line), line.quantity + 1)}>+</button>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="price">
                {line.product.priceCents > 0 ? formatMoney(line.product.priceCents * line.quantity, currency) : '—'}
              </div>
              <button
                type="button"
                className="link-underline text-muted"
                style={{ fontSize: 'var(--fs-xs)', marginTop: 'var(--space-2)' }}
                onClick={() => remove(itemKey(line))}
              >
                {t('cart.remove')}
              </button>
            </div>
          </div>
          ),
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'var(--space-5)' }}>
          <span style={{ fontSize: 'var(--fs-md)' }}>{t('cart.subtotal')}</span>
          <strong className="price" style={{ fontSize: 'var(--fs-md)' }}>{formatMoney(subtotalCents, currency)}</strong>
        </div>
        <p className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('cart.note')}</p>
        <div className="mt-6" style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <Link className="btn" to={lp('/checkout')}>{t('cart.checkout')}</Link>
          <Link className="btn btn--ghost" to={lp('/shop')}>{t('common.keepExploring')}</Link>
        </div>
      </div>
    </div>
  );
}
